/**
 * Phase 7 W-B — Coefficients regression broker.
 *
 * Wraps the coefficient-generation (2nd-order polynomial regression) and
 * proposal-approval endpoints so the Coefficients UI can propose plant-derived
 * A/B/C coefficients from the prior 12 billing periods and promote an approved
 * proposal into the active coefficients table (Schedule 17 §3.13.2 / §4.2).
 */
import AxiosManager from '@/utils/AxiosManager'
import Constants from '@/helpers/Constants'

export interface CoefficientProposal {
  id: number
  target_period: string
  month: string
  ai: number
  bi: number
  ci: number
  generated_at: string
  generated_by: string
  status: string
  data_points: number
  source_periods: string
  approved_by?: string | null
  approved_at?: string | null
}

export interface GenerateResult {
  target_period: string
  month: string
  ai: number
  bi: number
  ci: number
  data_points: number
  source_periods: string[]
  proposal_id: number
}

export default class CoefficientsBroker {
  private static get ax(): AxiosManager {
    return new AxiosManager(Constants.SERVER_URL)
  }

  /**
   * Run polynomial regression to propose new coefficients for a target period.
   */
  public static async generate(billing_period_id: number): Promise<GenerateResult> {
    return await CoefficientsBroker.ax.post('/generate_coefficients', { billing_period_id })
  }

  /**
   * List coefficient proposals (optionally filtered by status).
   */
  public static async listProposals(status?: string | null): Promise<CoefficientProposal[]> {
    let url = '/coefficient_proposals'
    if (status) {
      url += `?status=${encodeURIComponent(status)}`
    }
    const res = await CoefficientsBroker.ax.get(url)
    return res ?? []
  }

  /**
   * Approve a proposal and promote it into the active coefficients table.
   */
  public static async approveProposal(proposal_id: number): Promise<CoefficientProposal> {
    return await CoefficientsBroker.ax.post('/approve_coefficient_proposal', { proposal_id })
  }

  /**
   * Billing periods for the target-period dropdown.
   */
  public static async getBillingPeriods(): Promise<any[]> {
    const res = await CoefficientsBroker.ax.get('/get?comp=billing_periods')
    return res ?? []
  }
}
